import { useState, useEffect } from 'react';
import './App.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setTokenProvider, setAuthErrorHandler } from './services/apiClient';

import { DocumentUploader } from './components/upload/DocumentUploader';
import { TopicExtractor } from './components/upload/TopicExtractor';
import { ManualTopicEntry } from './components/upload/ManualTopicEntry';
import { CardGrid } from './components/cards/CardGrid';
import { CardFilters } from './components/cards/CardFilters';
import { CardEditor } from './components/cards/CardEditor';
import { PDFExporter } from './components/export/PDFExporter';
import { CurriculumBrowser } from './components/curriculum/CurriculumBrowser';
import { AuthModal } from './components/auth/AuthModal';
import { ModelEvaluator } from './components/cards/ModelEvaluator';
import { AdminPanel } from './components/admin/AdminPanel';
import { AiDisclaimer } from './components/AiDisclaimer';

import { Topic, GenerationRequest } from './types/Topic';
import { Card } from './types/Card';
import { CurriculumTopic, PreGeneratedCard } from './types/Curriculum';

import { useCardManagement } from './hooks/useCardManagement';
import { useCardFilters } from './hooks/useCardFilters';
import { apiClient } from './services/apiClient';

import './components/upload/DocumentUploader.css';
import './components/upload/TopicExtractor.css';
import './components/upload/ManualTopicEntry.css';
import './components/cards/CardGrid.css';
import './components/cards/CardFilters.css';
import './components/cards/CardEditor.css';
import './components/export/PDFExporter.css';
import './components/curriculum/CurriculumBrowser.css';
import './components/auth/AuthModal.css';
import './components/cards/ModelEvaluator.css';
import './components/admin/AdminPanel.css';
import './components/AiDisclaimer.css';

type Tab = 'browse' | 'upload' | 'my-cards' | 'admin';

function AppInner() {
  const { user, isAuthenticated, signOut, quota, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('browse');
  const [authModal, setAuthModal] = useState<{ open: boolean; reason?: string }>({ open: false });
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(
    () => localStorage.getItem('ai-disclaimer-accepted') === 'true'
  );

  const acceptDisclaimer = () => {
    localStorage.setItem('ai-disclaimer-accepted', 'true');
    setDisclaimerAccepted(true);
  };

  // Wire Cognito token into the API client
  useEffect(() => {
    setTokenProvider(() => user?.idToken ?? null);
    setAuthErrorHandler(() => signOut());
  }, [user]);

  const [extractedTopics, setExtractedTopics] = useState<Topic[]>([]);
  const [finalTopics, setFinalTopics] = useState<Topic[]>([]);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [showExporter, setShowExporter] = useState(false);
  const [showEvaluator, setShowEvaluator] = useState(false);

  const { cards, setCards, togglePin, regenerateCard, updateCard, isRegenerating, error: cardError } = useCardManagement();
  const { filteredCards, searchTerm, setSearchTerm, selectedSubject, setSelectedSubject, showPinnedOnly, setShowPinnedOnly, hasActiveFilters } = useCardFilters(cards);

  const showAuth = (reason?: string) => setAuthModal({ open: true, reason });

  const handleGenerateFromCurriculum = async (topics: CurriculumTopic[], selectedCards: PreGeneratedCard[]) => {
    setIsGenerating(true);
    setError('');
    try {
      const response = await apiClient.generateFromCurriculum(topics, selectedCards);
      const validCards = (response.cards || []).filter((c: any) => c.prompts && !c.error);
      if (validCards.length === 0) {
        setError('Card generation failed. Please try again.');
      } else {
        setCards(validCards);
        setActiveTab('my-cards');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate cards');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateFromTopics = async () => {
    if (!isAuthenticated) { showAuth('Sign in to generate your brain break cards'); return; }
    if (finalTopics.length === 0) return;
    setIsGenerating(true);
    setError('');
    try {
      const request: GenerationRequest = {
        topics: finalTopics,
        options: { regenerateUnpinned: false },
      };
      const response = await apiClient.generateCards(request);
      const validCards = (response.cards || []).filter((c: any) => c.prompts && !c.error);
      if (validCards.length === 0) {
        setError('Card generation failed. Please try again.');
      } else {
        setCards(validCards);
        setActiveTab('my-cards');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate cards');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCardRegenerate = async (cardId: string) => {
    try { await regenerateCard(cardId); }
    catch (err: any) { setError(err.message || 'Failed to regenerate card'); }
  };

  const resetCards = () => {
    setCards([]);
    setFinalTopics([]);
    setExtractedTopics([]);
    setActiveTab('browse');
  };

  const displayError = error || cardError;

  return (
    <div className="App">

      {/* ── Header + nav ── */}
      <header className="App-header">
        <div className="header-brand">
          <span className="header-logo">🧠</span>
          <div>
            <h1>Brain Break Cards</h1>
            <p>Curiosity-driven breaks for every learner</p>
          </div>
        </div>

        <nav className="header-nav">
          <button className={`nav-btn ${activeTab === 'browse' ? 'active' : ''}`} onClick={() => setActiveTab('browse')}>
            Browse Library
          </button>
          <button className={`nav-btn ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => setActiveTab('upload')}>
            Upload Syllabus
          </button>
          {cards.length > 0 && (
            <button className={`nav-btn ${activeTab === 'my-cards' ? 'active' : ''}`} onClick={() => setActiveTab('my-cards')}>
              My Cards <span className="nav-badge">{cards.length}</span>
            </button>
          )}
          {isAdmin && (
            <button className={`nav-btn ${activeTab === 'admin' ? 'active' : ''}`} onClick={() => setActiveTab('admin')}>
              ⚙️ Admin
            </button>
          )}
        </nav>

        <div className="header-auth">
          {isAuthenticated ? (
            <div className="auth-status">
              {quota && (
                <span className="quota-pill" title={`${quota.generationsUsed} of ${quota.generationsLimit} generations used this month`}>
                  {quota.generationsLimit - quota.generationsUsed} left
                </span>
              )}
              <span className="auth-email">{user?.email}</span>
              <button className="auth-signout" onClick={signOut}>Sign out</button>
            </div>
          ) : (
            <button className="auth-signin-btn" onClick={() => showAuth()}>Sign in</button>
          )}
        </div>
      </header>

      <main className="App-main">
        {displayError && (
          <div className="error-message">
            <span>{displayError}</span>
            <button onClick={() => setError('')} className="error-close">✕</button>
          </div>
        )}

        {isGenerating && (
          <div className="generating-overlay">
            <div className="generating-spinner">
              <span>🧠</span>
              <p>Generating your brain break cards…</p>
            </div>
          </div>
        )}

        {/* ── Browse tab ── */}
        {activeTab === 'browse' && (
          <>
            <div className="hero-section">
              <h2>Brain Break Cards for Every Learner</h2>
              <p>
                Curiosity-driven, inquiry-based break activities matched to your curriculum.
                Browse our ready-made library or upload your own syllabus to generate custom cards.
              </p>
              <div className="hero-actions">
                <button className="hero-cta primary" onClick={() => setActiveTab('upload')}>
                  📄 Upload Your Syllabus
                </button>
                <button className="hero-cta secondary" onClick={() => {
                  document.querySelector('.curriculum-browser')?.scrollIntoView({ behavior: 'smooth' });
                }}>
                  📚 Browse Library
                </button>
              </div>
            </div>
            <CurriculumBrowser
              onGenerateCards={handleGenerateFromCurriculum}
              onShowAuth={showAuth}
            />
          </>
        )}

        {/* ── Upload tab ── */}
        {activeTab === 'upload' && !isAuthenticated && (
          <div className="empty-cards">
            <p>You need to sign in to upload a syllabus and generate custom cards.</p>
            <button className="generate-button" onClick={() => showAuth('Sign in to upload your syllabus')}>Sign In</button>
          </div>
        )}
        {activeTab === 'upload' && isAuthenticated && (
          <div className="upload-flow">
            <section className="upload-section">
              <DocumentUploader
                onTopicsExtracted={topics => { setExtractedTopics(topics); setFinalTopics(topics); setError(''); }}
                onError={setError}
              />
            </section>

            <div className="divider"><span className="divider-text">OR</span></div>

            <section className="manual-entry-section">
              {!showManualEntry ? (
                <div className="manual-entry-prompt">
                  <h3>Add topics manually</h3>
                  <p>Skip document upload and type topics directly</p>
                  <button onClick={() => setShowManualEntry(true)} className="manual-entry-button">
                    Add Topics Manually
                  </button>
                </div>
              ) : (
                <ManualTopicEntry
                  onTopicAdded={topic => { const updated = [...finalTopics, topic]; setFinalTopics(updated); setExtractedTopics(updated); }}
                  existingTopics={finalTopics}
                />
              )}
            </section>

            {extractedTopics.length > 0 && (
              <section className="topics-section">
                <TopicExtractor extractedTopics={extractedTopics} onTopicsUpdated={setFinalTopics} />
              </section>
            )}

            {finalTopics.length > 0 && (
              <section className="actions-section">
                <div className="topic-summary">
                  <h3>Ready to Generate</h3>
                  <p>{finalTopics.length} topic{finalTopics.length !== 1 ? 's' : ''} ready</p>
                  <button className="generate-button" onClick={handleGenerateFromTopics} disabled={isGenerating}>
                    {isGenerating ? 'Generating…' : isAuthenticated ? 'Generate Cards' : 'Sign in to Generate'}
                  </button>
                  {isAdmin && (
                    <button
                      className="generate-button secondary"
                      onClick={() => setShowEvaluator(true)}
                      disabled={isGenerating}
                      title="Run all 3 models in parallel and compare results"
                    >
                      Compare Models
                    </button>
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        {/* ── My Cards tab ── */}
        {activeTab === 'my-cards' && (
          <>
            {cards.length === 0 ? (
              <div className="empty-cards">
                <p>No cards yet. Browse the library or upload a syllabus to get started.</p>
                <button className="generate-button" onClick={() => setActiveTab('browse')}>Browse Library</button>
              </div>
            ) : (
              <>
                <section className="cards-section">
                  <div className="cards-header">
                    <h2>Your Brain Break Cards</h2>
                    <div className="cards-stats">
                      <span>{cards.length} card{cards.length !== 1 ? 's' : ''}</span>
                      {hasActiveFilters && <span className="filtered-count">({filteredCards.length} shown)</span>}
                    </div>
                  </div>

                  <CardFilters
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    selectedSubject={selectedSubject}
                    onSubjectChange={setSelectedSubject}
                    showPinnedOnly={showPinnedOnly}
                    onPinnedOnlyChange={setShowPinnedOnly}
                  />

                  {isRegenerating && <div className="regenerating-indicator"><span>Regenerating card…</span></div>}

                  <CardGrid
                    cards={filteredCards}
                    onCardPin={togglePin}
                    onCardRegenerate={handleCardRegenerate}
                    onCardEdit={setEditingCard}
                  />

                  {filteredCards.length === 0 && hasActiveFilters && (
                    <div className="no-results"><p>No cards match your filters.</p></div>
                  )}
                </section>

                <section className="actions-section">
                  <button className="export-button-primary" onClick={() => setShowExporter(true)}>Export to PDF</button>
                  <button className="generate-button secondary" onClick={resetCards}>Start Over</button>
                </section>
              </>
            )}
          </>
        )}

        {/* ── Admin tab ── */}
        {activeTab === 'admin' && <AdminPanel />}
      </main>

      {editingCard && (
        <CardEditor
          card={editingCard}
          onSave={updates => { updateCard(editingCard.id, updates); setEditingCard(null); }}
          onCancel={() => setEditingCard(null)}
        />
      )}

      {showExporter && <PDFExporter cards={cards} onClose={() => setShowExporter(false)} />}

      {showEvaluator && finalTopics.length > 0 && (
        <ModelEvaluator
          topics={finalTopics}
          onSelectCards={cards => { setCards(cards); setActiveTab('my-cards'); }}
          onClose={() => setShowEvaluator(false)}
        />
      )}

      {!disclaimerAccepted && <AiDisclaimer onAccept={acceptDisclaimer} />}

      {authModal.open && (
        <AuthModal
          reason={authModal.reason}
          onClose={() => setAuthModal({ open: false })}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
